import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { Plus, Search, Edit2, Trash2, X, Receipt, FileDown, CheckCircle2, Repeat, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';

const CATEGORIAS = ['Fornecedores', 'Aluguel', 'Impostos e Taxas', 'Salários', 'Manutenção', 'Combustível', 'Seguros', 'Serviços', 'Outros'];
const FORMAS_PAG = ['Pix', 'Cartão', 'Dinheiro', 'Transferência', 'Boleto', 'Depósito'];

const CAT_CLS = {
  'Fornecedores':      'bg-indigo-100 text-indigo-700',
  'Aluguel':           'bg-pink-100 text-pink-700',
  'Impostos e Taxas':  'bg-red-100 text-red-700',
  'Salários':          'bg-cyan-100 text-cyan-700',
  'Manutenção':        'bg-orange-100 text-orange-700',
  'Combustível':       'bg-yellow-100 text-yellow-700',
  'Seguros':           'bg-teal-100 text-teal-700',
  'Serviços':          'bg-blue-100 text-blue-700',
  'Outros':            'bg-slate-100 text-slate-600',
};

const STATUS_CFG = {
  a_vencer: { label: 'A Vencer', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  vencido:  { label: 'Vencido',  cls: 'bg-red-100 text-red-700 border-red-300' },
  pago:     { label: 'Pago',     cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  descricao: '',
  fornecedor: '',
  categoria: 'Fornecedores',
  valor: '',
  data_emissao: '',
  data_vencimento: today(),
  forma_pagamento_prevista: 'Pix',
  viagem_id: '',
  recorrente: false,
  observacoes: '',
};

const emptyPagamento = { data_pagamento: today(), forma_pagamento: 'Pix' };

function fmtVal(v) {
  return v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';
}
function fmtDate(d) {
  if (!d) return '-';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

export default function ContasPagar() {
  const { user } = useAuth();
  const { showLoading, hideLoading } = useLoading();
  const canWrite = ['admin', 'financeiro'].includes(user?.role);

  const [contas, setContas] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [payingConta, setPayingConta] = useState(null);
  const [pagamentoForm, setPagamentoForm] = useState(emptyPagamento);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (catFilter) params.categoria = catFilter;
      if (statusFilter) params.status = statusFilter;
      const [{ data: cp }, { data: viag }] = await Promise.all([
        api.get('/contas-pagar', { params }),
        api.get('/viagens', { params: { limit: 500 } }),
      ]);
      setContas(cp);
      setViagens(viag.viagens || viag || []);
    } catch {
      toast.error('Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  }, [search, catFilter, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setForm({ ...emptyForm, data_vencimento: today() });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setForm({
      descricao: c.descricao || '',
      fornecedor: c.fornecedor || '',
      categoria: c.categoria || 'Outros',
      valor: c.valor != null ? String(c.valor) : '',
      data_emissao: c.data_emissao || '',
      data_vencimento: c.data_vencimento || today(),
      forma_pagamento_prevista: c.forma_pagamento_prevista || 'Pix',
      viagem_id: c.viagem_id || '',
      recorrente: !!c.recorrente,
      observacoes: c.observacoes || '',
    });
    setEditingId(c.id);
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.descricao.trim()) { toast.error('Informe a descrição'); return; }
    if (!form.valor || isNaN(Number(form.valor)) || Number(form.valor) <= 0) {
      toast.error('Informe um valor válido'); return;
    }
    if (!form.data_vencimento) { toast.error('Informe a data de vencimento'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor: Number(form.valor),
        viagem_id: form.viagem_id || null,
        data_emissao: form.data_emissao || null,
      };
      if (editingId) {
        await api.put(`/contas-pagar/${editingId}`, payload);
        toast.success('Conta atualizada');
      } else {
        await api.post('/contas-pagar', payload);
        toast.success('Conta lançada');
      }
      setModalOpen(false);
      fetchAll();
    } catch {
      toast.error('Erro ao salvar conta a pagar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    showLoading('Excluindo...');
    try {
      await api.delete(`/contas-pagar/${deleteId}`);
      toast.success('Conta excluída');
      setDeleteId(null);
      fetchAll();
    } catch {
      toast.error('Erro ao excluir');
    } finally {
      hideLoading();
    }
  };

  const openPagar = (c) => {
    setPagamentoForm({ data_pagamento: today(), forma_pagamento: c.forma_pagamento_prevista || 'Pix' });
    setPayingConta(c);
  };

  const confirmPagamento = async () => {
    if (!payingConta) return;
    showLoading('Registrando pagamento...');
    try {
      await api.put(`/contas-pagar/${payingConta.id}`, {
        pago: true,
        data_pagamento: pagamentoForm.data_pagamento,
        forma_pagamento: pagamentoForm.forma_pagamento,
      });
      toast.success(payingConta.recorrente ? 'Pagamento registrado — próxima ocorrência gerada' : 'Pagamento registrado');
      setPayingConta(null);
      fetchAll();
    } catch {
      toast.error('Erro ao registrar pagamento');
    } finally {
      hideLoading();
    }
  };

  const exportXLSX = () => {
    const rows = contas.map(c => ({
      'Vencimento': fmtDate(c.data_vencimento),
      'Status': STATUS_CFG[c.status]?.label || c.status,
      'Fornecedor': c.fornecedor || '-',
      'Descrição': c.descricao || '-',
      'Categoria': c.categoria || '-',
      'Viagem': viagens.find(v => v.id === c.viagem_id)?.titulo || '-',
      'Valor': Number(c.valor) || 0,
      'Recorrente': c.recorrente ? 'Sim' : 'Não',
      'Data Pagamento': c.data_pagamento ? fmtDate(c.data_pagamento) : '-',
      'Forma Pagamento': c.forma_pagamento || c.forma_pagamento_prevista || '-',
      'Observações': c.observacoes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [12, 10, 22, 30, 18, 24, 12, 10, 14, 16, 30].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contas a Pagar');
    XLSX.writeFile(wb, `contas_a_pagar_${today()}.xlsx`);
  };

  const totalAVencer = contas.filter(c => c.status === 'a_vencer').reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const totalVencido = contas.filter(c => c.status === 'vencido').reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const totalPago = contas.filter(c => c.status === 'pago').reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const countVencido = contas.filter(c => c.status === 'vencido').length;
  const getViagem = (id) => viagens.find(v => v.id === id);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow" style={{ background: 'linear-gradient(135deg,#0147a0,#0163d2)' }}>
            <Receipt className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Contas a Pagar</h1>
            <p className="text-xs text-slate-500">Controle de obrigações e vencimentos</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportXLSX} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
            <FileDown className="w-4 h-4" /> Excel
          </button>
          {canWrite && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow transition-all" style={{ background: 'linear-gradient(135deg,#0147a0,#0163d2)' }}>
              <Plus className="w-4 h-4" /> Nova Conta
            </button>
          )}
        </div>
      </div>

      {countVencido > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">
            <span className="font-bold">{countVencido}</span> {countVencido === 1 ? 'conta vencida' : 'contas vencidas'} somando <span className="font-bold">{fmtVal(totalVencido)}</span>
          </p>
        </div>
      )}

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 mb-1">A Vencer</p>
          <p className="text-2xl font-bold text-amber-600">{fmtVal(totalAVencer)}</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-red-200 p-4 shadow-sm bg-red-50/40">
          <p className="text-xs text-red-500 mb-1 font-medium">Vencido</p>
          <p className="text-2xl font-bold text-red-600">{fmtVal(totalVencido)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 mb-1">Pago</p>
          <p className="text-2xl font-bold text-emerald-600">{fmtVal(totalPago)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
          <p className="text-xs text-slate-500 mb-1">Lançamentos</p>
          <p className="text-2xl font-bold text-slate-700">{contas.length}</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[180px] bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            className="bg-transparent text-sm w-full outline-none text-slate-700 placeholder-slate-400"
            placeholder="Buscar descrição ou fornecedor..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 outline-none"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="a_vencer">A Vencer</option>
          <option value="vencido">Vencido</option>
          <option value="pago">Pago</option>
        </select>
        <select
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 outline-none"
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
        >
          <option value="">Todas as categorias</option>
          {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(catFilter || statusFilter) && (
          <button onClick={() => { setCatFilter(''); setStatusFilter(''); }} className="text-xs text-slate-400 hover:text-slate-600 px-2">Limpar</button>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Carregando...</div>
        ) : contas.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Nenhuma conta a pagar encontrada</p>
            <p className="text-slate-400 text-sm mt-1">{canWrite ? 'Clique em "Nova Conta" para lançar a primeira obrigação.' : 'Nenhum lançamento neste período.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vencimento</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Fornecedor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Descrição</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Categoria</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Valor</th>
                  {canWrite && <th className="px-4 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {contas.map((c) => {
                  const viag = getViagem(c.viagem_id);
                  const isVencido = c.status === 'vencido';
                  return (
                    <tr key={c.id} className={`hover:bg-slate-50/60 transition-colors ${isVencido ? 'bg-red-50/40' : ''}`}>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {fmtDate(c.data_vencimento)}
                          {c.recorrente && <Repeat className="w-3 h-3 text-slate-400" title="Conta recorrente (mensal)" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_CFG[c.status]?.cls || 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                          {STATUS_CFG[c.status]?.label || c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{c.fornecedor || '-'}</td>
                      <td className="px-4 py-3 text-slate-700 font-medium max-w-[220px] truncate">
                        {c.descricao}
                        {viag && <span className="block text-xs text-slate-400 truncate">{viag.titulo}</span>}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${CAT_CLS[c.categoria] || 'bg-slate-100 text-slate-600'}`}>
                          {c.categoria}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-700 whitespace-nowrap">{fmtVal(c.valor)}</td>
                      {canWrite && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            {c.status !== 'pago' && (
                              <button onClick={() => openPagar(c)} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title="Marcar como paga">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setDeleteId(c.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal form */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg">{editingId ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Descrição *</label>
                <input required className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="Ex: Conta de internet do escritório" value={form.descricao} onChange={e => f('descricao', e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Fornecedor</label>
                  <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="Nome do fornecedor/credor" value={form.fornecedor} onChange={e => f('fornecedor', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Categoria *</label>
                  <select required className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.categoria} onChange={e => f('categoria', e.target.value)}>
                    {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Valor (R$) *</label>
                  <input required type="number" step="0.01" min="0.01" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="0,00" value={form.valor} onChange={e => f('valor', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Forma de Pagamento Prevista</label>
                  <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.forma_pagamento_prevista} onChange={e => f('forma_pagamento_prevista', e.target.value)}>
                    {FORMAS_PAG.map(fp => <option key={fp} value={fp}>{fp}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Data de Emissão</label>
                  <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.data_emissao} onChange={e => f('data_emissao', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Data de Vencimento *</label>
                  <input required type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.data_vencimento} onChange={e => f('data_vencimento', e.target.value)} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Vincular a Viagem <span className="font-normal text-slate-400">(opcional)</span></label>
                <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" value={form.viagem_id} onChange={e => f('viagem_id', e.target.value)}>
                  <option value="">— Conta geral —</option>
                  {viagens.map(v => (
                    <option key={v.id} value={v.id}>{v.titulo} ({v.data ? v.data.split('-').reverse().join('/') : ''})</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/30" checked={form.recorrente} onChange={e => f('recorrente', e.target.checked)} />
                <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <Repeat className="w-3.5 h-3.5 text-slate-400" />
                  Repete todo mês
                </span>
              </label>
              {form.recorrente && (
                <p className="text-xs text-slate-400 -mt-2 pl-6">Ao marcar esta conta como paga, a próxima ocorrência do mês seguinte será criada automaticamente.</p>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Observações</label>
                <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none" placeholder="Detalhes adicionais..." value={form.observacoes || ''} onChange={e => f('observacoes', e.target.value)} />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold text-white shadow transition-all disabled:opacity-60" style={{ background: 'linear-gradient(135deg,#0147a0,#0163d2)' }}>
                  {saving ? 'Salvando...' : editingId ? 'Salvar Alterações' : 'Lançar Conta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal marcar como paga */}
      {payingConta && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1 text-center">Marcar como paga</h3>
            <p className="text-slate-500 text-sm mb-5 text-center">{payingConta.descricao} — {fmtVal(payingConta.valor)}</p>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Data do Pagamento</label>
                <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400" value={pagamentoForm.data_pagamento} onChange={e => setPagamentoForm(p => ({ ...p, data_pagamento: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Forma de Pagamento</label>
                <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400" value={pagamentoForm.forma_pagamento} onChange={e => setPagamentoForm(p => ({ ...p, forma_pagamento: e.target.value }))}>
                  {FORMAS_PAG.map(fp => <option key={fp} value={fp}>{fp}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setPayingConta(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
              <button onClick={confirmPagamento} className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar delete */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-2">Excluir conta a pagar?</h3>
            <p className="text-slate-500 text-sm mb-6">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
